import {CardState, ICard, IGameState, IPile} from "../redux/types";
import { cardName } from "../card_index";
import _last from 'lodash/last';
import {
  canDrop,
  canDropToHome,
  getCardsFormState,
  getCardsRenderInfo,
  getCardYInPile,
  getDropList,
  getHomeX,
  getNumberOfMaxCardsCanBeDropped,
  getPileX,
  getSlotY,
  isSingleCard
} from "../utils";
import {shape} from "../interfaces";

export type DropListItem = {
  name: cardName,
  state?: CardState,
  index?: number,
  cardIndex?: number,
  rate: number,
  color: 'red' | 'black',
  putWrong: boolean,
  pileSize?: number,
}

export interface IDropElement {
  state: CardState;
  index: number;
  dropList: DropListItem[];
  pileSize?: number;
}

export interface IHint {
  cardSelector: cardName,
  dropSelector: string,
  dropElement: IDropElement,
}

export type Hints = IHint[];

export function getDragCards(state: IGameState): IPile {
  const cards: IPile = getCardsFormState(state);
  return cards.filter(card => card.draggable);
}

export function getDropElements(state: IGameState): IDropElement[] {
  const { laid, slots, home } = state;
  const dragCards = getDragCards(state);
  const drops: IDropElement[] = [];

  home.forEach((homeSlot, index) => {
    const lastCard = _last(homeSlot);
    if (lastCard && lastCard.value === 'king') return;
    const cards: IPile = dragCards.filter((card: ICard) => {
      return canDropToHome(lastCard, card) && isSingleCard(card);
    });
    if (!cards.length) return;
    const dropList = getDropList(cards);
    const dropElement: IDropElement = { state: CardState.home, index, dropList, };
    drops.push(dropElement);
  });

  laid.forEach((pile, index) => {
    const card = _last(pile);

    const dropElement: IDropElement = { state: CardState.laid, index, dropList: [], pileSize: pile.length };
    const dragCardForThisPile = dragCards.filter((_card) => {
      return Boolean(card && canDrop(_card, card)
        && ( _card.state === CardState.slots || (_card.state === CardState.laid && _card.index !== index) ));
    });
    dropElement.dropList = card ? getDropList(dragCardForThisPile) : getDropList(dragCards);
    dropElement.dropList.length && drops.push(dropElement);
  });

  slots.forEach((slot, index) => {
    if (slot !== null) return;
    const cards = dragCards.filter(card => isSingleCard(card));
    const dropList = getDropList(cards);
    const dropElement: IDropElement = { state: CardState.slots, index, dropList };
    drops.push(dropElement);
  });

  return drops;
}

export function getHintList(dropElements: IDropElement[], numberOfEmptyPiles: number, numberOfEmptySlots: number): Hints {
  const hints: Hints = [];

  dropElements.forEach((dropElement: IDropElement) => {
    const { state, index, dropList } = dropElement;

    const list = (state === CardState.laid) ? dropList : /*[*/ dropList/*[0] ]*/;
    list.forEach((item: DropListItem) => {
      if (!item.putWrong && item.state === CardState.laid) return;

      if (state === CardState.laid && item.state === CardState.laid) {
        const maxCards = getNumberOfMaxCardsCanBeDropped(numberOfEmptySlots, numberOfEmptyPiles, !Boolean(dropElement.pileSize));
        const pileLength = (item.pileSize || 0) - (item.cardIndex || 0);

        if (pileLength > maxCards) return;
      }

      if (state === CardState.slots && item.state === CardState.slots) return;

      hints.push({ cardSelector: item.name, dropSelector: `${state}-${index}`, dropElement, });
    });
  });

  return hints;
}

export function makeHints(dropElements: IDropElement[], numberOfEmptyPiles: number, numberOfEmptySlots: number): Hints {
  return getHintList(dropElements, numberOfEmptyPiles, numberOfEmptySlots);
}

export function getDropElementPosition(dropElement: IDropElement, size: number): shape {
  const cardsInfo = getCardsRenderInfo(size);
  const result: shape = { x: 0, y: 0, width: cardsInfo.width, height: cardsInfo.height };
  const { state, index, pileSize } = dropElement;

  switch (state) {
    case CardState.laid: {
      const pileS = pileSize || 0;
      result.x = getPileX(index, size);
      result.y = getCardYInPile(pileS + 1, size);
      break;
    }
    case CardState.slots: {
      result.y = getSlotY(size);
      result.x = getPileX(index, size);
      break;
    }
    case CardState.home: {
      result.y = getSlotY(size);
      result.x = getHomeX(index, size);
      break;
    }
  }

  return result;
}

export function getHintPosition(hint: IHint, size: number): shape {
  return getDropElementPosition(hint.dropElement, size);
}